import { useEffect, useRef, useState } from 'react'

import cn from 'classnames'

import { useRouter } from 'next/router'

import { useTranslation } from 'utils/use-translation'

import { goToChat } from 'utils/go-to-chat'

import styles from '../styles.module.scss'
import RabbitIcon from '../images/Rabbit+windows.png'
import FormUnderlayer from '../images/form underlayer.svg'
import ButtonBlue from '../../../components/ui/button/_color/_blue'
import FormUnderlayerMobile from '../images/illustration.svg'
import InputPhone from '../../../components/app/input-phone'
import {
  EMessageTypes,
  fetchReferral,
  getVerifiedTokenRateLimited,
  resendVerificationCode,
  sendVerificationCode,
  sendVerificationCodeWithoutCaptcha,
} from '../../../store/fetchers/utils'
import { Captcha } from '../../../components/app/captcha'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { LoginStatuses, selectedAuthStatus, selectedUser, setUserAuthToken } from '../../../store/reducers/users'
import { signInRateLimited } from '../../../store/fetchers/auth'
import Loader from '../../../components/ui/loader'
import { clearCaptchaData, getReferralId, saveReferralId } from '../../../libs/local-storage'
import { useHeader } from '../../../utils/use-header'
import CodeVerificationInput from '../../../components/global/code-verification-input'
import useLocationSearch from 'libs/location-search'
import { getButtonName, getSubtitle, getSwitchName, getTitle } from './utils'

const TIMER_RESEND = 60

export default function UserSignIn() {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const header = useHeader('Sign in')
  const query = useLocationSearch()
  const referralId = Array.isArray(query.referral) ? query.referral[0] : query.referral
  const user = useAppSelector(selectedUser)
  const authStatus = useAppSelector(selectedAuthStatus)

  const [screenWidth, setScreenWidth] = useState<number>(null)
  const [isPhone, setIsPhone] = useState<boolean>(false)
  const phoneRef = useRef<string>('')
  const emailRef = useRef<string>('')
  const [backupEmail, setEmailBackup] = useState<string>('')
  const [is2faGoogleApp, setIs2faGoogleApp] = useState<boolean>(true)
  const isPhoneRef = useRef<boolean>(false)

  const [emailErr, setEmailErr] = useState<string>(null)
  const [phoneErr, setPhoneErr] = useState<string>(null)

  const isActive2faRef = useRef<boolean>(null)
  const phoneVerificationCode = useRef<string>(null)
  const emailVerificationCode = useRef<string>(null)
  const emailVerifiedToken = useRef<string>('')
  const phoneVerifiedToken = useRef<string>('')
  const backupEmailVerificationToken = useRef<string>('')

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [code, setCode] = useState<string>('')

  const [code2fa, setCode2fa] = useState<string>('')

  const [isPhoneCodeValid, setIsPhoneCodeValid] = useState<boolean>(true)
  const [isEmailCodeValid, setIsEmailCodeValid] = useState<boolean>(true)
  const [isValid2faCode, setIsValid2faCode] = useState(true)
  const [rateLimitErr, setRateLimitErr] = useState<string>(null)

  const [referralAction, setReferralAction] = useState(null)

  const router = useRouter()

  const [countSeconds, setCountSeconds] = useState(TIMER_RESEND)
  const countSecondsRef = useRef<number>(TIMER_RESEND)
  const resendTimerRef = useRef(null)
  const codeBtnRef = useRef(null)

  const externalReferralCode = getReferralId()

  const restartResendTimer = () => {
    clearTimeout(resendTimerRef.current)

    setCountSeconds(TIMER_RESEND)
    countSecondsRef.current = TIMER_RESEND
    resendTimerRef.current = setInterval(() => {
      setCountSeconds((count) => {
        const newCount = Math.max(0, count - 1)
        return newCount
      })
      countSecondsRef.current = Math.max(0, countSecondsRef.current - 1)
    }, 1000)
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setScreenWidth(window.innerWidth)
    }
    clearCaptchaData()
  }, [])

  useEffect(() => {
    if (code.length === 6) {
      if (isPhoneRef.current) {
        sendPhoneCode()
      } else {
        sendEmailCode()
      }
    }
  }, [code])

  const validateReferral = async (referralId: string) => {
    try {
      const { referral_action } = await fetchReferral({ referral_code: referralId })

      if (referral_action) {
        setReferralAction(referral_action)
      }
    } catch (error) {}
  }

  useEffect(() => {
    if (!referralId) return

    saveReferralId(referralId)

    if (!user && authStatus !== LoginStatuses.LOGIN && authStatus) {
      validateReferral(referralId)
    }
  }, [referralId, user, authStatus])

  useEffect(() => {
    if (!is2faGoogleApp) getBackupEmailCode()
  }, [is2faGoogleApp])

  const onPhoneChange = (val: string): void => {
    if (phoneErr) {
      setPhoneErr(null)
    }
    phoneRef.current = val
  }

  const onEmailChange = (evt): void => {
    if (emailErr) {
      setEmailErr(null)
    }
    const val = evt.target.value
    emailRef.current = val
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      codeBtnRef.current.click()
    }
  }

  const switchSignUp = (): void => {
    setEmailErr(null)
    setPhoneErr(null)
    emailRef.current = ''
    phoneRef.current = ''
    setIsPhone((val) => !val)
    isPhoneRef.current = !isPhoneRef.current
  }

  const getCode = async (captcha: string): Promise<void> => {
    setIsLoading(true)

    const { tokens, isActive2fa, error, backupEmail } = await sendVerificationCode({
      ...(!isPhoneRef.current ? { email: emailRef.current } : { phone: phoneRef.current }),
      captcha: captcha,
      type: EMessageTypes.MONITOR_LIST,
    })

    isActive2faRef.current = isActive2fa
    setEmailBackup(backupEmail)

    if (isPhoneRef.current) {
      phoneVerificationCode.current = tokens[0]
    } else {
      emailVerificationCode.current = tokens[0]
    }

    const result = !!emailVerificationCode.current || !!phoneVerificationCode.current

    if (result) {
      setIsLoading(false)
      setStep(2)
      restartResendTimer()
    }

    if (isPhoneRef.current) {
      if (error === 'Network Error') {
        setPhoneErr(t('network_error'))
      } else if (error === 'Send Error') {
        setPhoneErr(t('send_sms_error'))
      } else if (error.includes('exceeded')) {
        setPhoneErr(error)
      } else {
        setPhoneErr(t('invalid_phone'))
      }
    } else {
      setEmailErr(error === 'Network Error' ? t('network_error') : t('invalid_email'))
    }

    setIsLoading(false)
  }

  const getBackupEmailCode = async (): Promise<void> => {
    if (backupEmailVerificationToken.current) return

    const res = await sendVerificationCodeWithoutCaptcha({
      backupEmail: backupEmail,
      type: EMessageTypes.MONITOR_LIST,
    })

    if (res.tokens.length) {
      backupEmailVerificationToken.current = res.tokens[0]
    }
  }

  const sendEmailCode = async () => {
    try {
      setIsLoading(true)
      if (code.length !== 6) {
        setIsEmailCodeValid(false)
        return
      }

      const res = await getVerifiedTokenRateLimited({
        code: code,
        verification_token: emailVerificationCode.current,
      })
      if (!res.result) {
        setRateLimitErr(res.message)
        setIsEmailCodeValid(false)
        setIsLoading(false)
      } else {
        setRateLimitErr(null)
        emailVerifiedToken.current = res.response
        if (emailVerifiedToken.current) {
          if (isActive2faRef.current) {
            setStep(3)
          } else {
            try {
              const res = await signInRateLimited({
                verification_token: emailVerifiedToken.current,
                code_2fa: null,
                referral_code: externalReferralCode,
              })
              if (!res.result) {
                setRateLimitErr(res.message)
              } else {
                setRateLimitErr(null)
                await dispatch(setUserAuthToken(res.response))
                router.push('/account/dashboard')
              }
              setIsLoading(false)
            } catch (err) {
              if (err.response && err.response.message.includes('Please try again')) {
                setRateLimitErr(err.response.message)
              } else {
                setRateLimitErr(t('incorrect_verification_code'))
              }
            }
          }
        }

        setIsEmailCodeValid(!!emailVerifiedToken.current)
        setIsLoading(false)
      }
    } catch (err) {
      if (err.response && err.response.message.includes('Please try again')) {
        setRateLimitErr(err.response.message)
      } else {
        setRateLimitErr(t('incorrect_verification_code'))
      }

      setIsEmailCodeValid(false)
      setIsLoading(false)
    }
  }

  const sendPhoneCode = async () => {
    try {
      setIsLoading(true)
      if (code.length !== 6) {
        setIsPhoneCodeValid(false)
        return
      }

      const res = await getVerifiedTokenRateLimited({
        code: code,
        verification_token: phoneVerificationCode.current,
      })
      if (!res.result) {
        setRateLimitErr(res.message)
        setIsPhoneCodeValid(false)
        setIsLoading(false)
      } else {
        phoneVerifiedToken.current = res.response
        setRateLimitErr(null)
        if (phoneVerifiedToken.current) {
          if (isActive2faRef.current) {
            setStep(3)
          } else {
            const res = await signInRateLimited({
              verification_token: phoneVerifiedToken.current,
              code_2fa: null,
              referral_code: externalReferralCode,
            })
            if (!res.result) {
              setRateLimitErr(res.message)
            } else {
              await dispatch(setUserAuthToken(res.response))
              setRateLimitErr(null)
              router.push('/account/dashboard')
            }

            setIsLoading(false)
          }
        }

        setIsPhoneCodeValid(!!phoneVerifiedToken.current)
        setIsLoading(false)
      }
    } catch (err) {
      if (err.response && err.response.message.includes('Please try again')) {
        setRateLimitErr(err.response.message)
      } else {
        setRateLimitErr(t('incorrect_verification_code'))
      }

      setIsPhoneCodeValid(false)
      setIsLoading(false)
    }
  }

  const send2faCode = async (code2fa: string) => {
    setIsLoading(true)
    let res: {
      result: boolean
      response: string
      message: string
    } = null

    if (backupEmailVerificationToken.current && !is2faGoogleApp) {
      try {
        const res = await getVerifiedTokenRateLimited({
          code: code2fa,
          verification_token: backupEmailVerificationToken.current,
        })

        if (!res.result) {
          setRateLimitErr(res.message)
          setIsValid2faCode(false)
          setIsLoading(false)
          return
        }
      } catch (err) {
        if (err.response && err.response.message.includes('Please try again')) {
          setRateLimitErr(err.response.message)
        } else {
          setRateLimitErr(t('incorrect_verification_code'))
        }
        setIsValid2faCode(false)
        setIsLoading(false)
        return
      }
    }

    if (isPhone) {
      try {
        res = await signInRateLimited({
          verification_token: backupEmailVerificationToken.current || phoneVerifiedToken.current,
          code_2fa: code2fa,
          referral_code: externalReferralCode,
        })

        if (!res.result) {
          setRateLimitErr(res.message)
          setIsValid2faCode(false)
          setIsLoading(false)
        } else {
          if (res.response) {
            await dispatch(setUserAuthToken(res.response))
            setRateLimitErr(null)
            router.push('/account/dashboard')
            setIsLoading(false)
          } else {
            setIsValid2faCode(false)
            setIsLoading(false)
          }
        }
      } catch (err) {
        if (err.response && err.response.message.includes('Please try again')) {
          setRateLimitErr(err.response.message)
        } else {
          setRateLimitErr(t('incorrect_verification_code'))
        }
        setIsValid2faCode(false)
        setIsLoading(false)
      }
    } else {
      try {
        res = await signInRateLimited({
          verification_token: backupEmailVerificationToken.current || emailVerifiedToken.current,
          code_2fa: code2fa,
          referral_code: externalReferralCode,
        })

        if (!res.result) {
          setRateLimitErr(res.message)
          setIsValid2faCode(false)
          setIsLoading(false)
        } else {
          if (res.response) {
            await dispatch(setUserAuthToken(res.response))
            setRateLimitErr(null)
            router.push('/account/dashboard')
            setIsLoading(false)
          } else {
            setIsValid2faCode(false)
            setIsLoading(false)
          }
        }
      } catch (err) {
        if (err.response && err.response.message.includes('Please try again')) {
          setRateLimitErr(err.response.message)
        } else {
          setRateLimitErr(t('incorrect_verification_code'))
        }
        setIsValid2faCode(false)
        setIsLoading(false)
      }
    }
  }

  const resendPhoneCode = async (captcha: string): Promise<void> => {
    setIsLoading(true)
    const token = await resendVerificationCode({
      verification_token: phoneVerificationCode.current,
      captcha: captcha,
    })

    phoneVerificationCode.current = token
    setIsLoading(false)
  }

  const resendEmailCode = async (captcha: string): Promise<void> => {
    setIsLoading(true)
    const token = await resendVerificationCode({
      verification_token: emailVerificationCode.current,
      captcha: captcha,
    })

    emailVerificationCode.current = token
    setIsLoading(false)
  }

  const requestNewCode = (captcha: string) => {
    if (countSecondsRef.current !== 0) {
      return
    }

    if (isPhone) {
      resendPhoneCode(captcha)
    } else {
      resendEmailCode(captcha)
    }
    restartResendTimer()
  }

  return (
    <section className={styles.auth}>
      {header}
      <img src={RabbitIcon} alt="rabbit icon" className={styles.rabbit} />
      <form className={cn(styles.form, step === 3 && styles.secVerificationForm)}>
        {step === 1 && (
          <>
            <h1>{t(getTitle(referralAction))}</h1>
            <p dangerouslySetInnerHTML={{
              __html: t(getSubtitle(referralAction))
            }}></p>

            <div className={styles.form__field}>
              <label className={cn({ [styles.error]: emailErr })} htmlFor="email">
                {t('your')} {isPhone ? t('phone_number') : t('email')}
              </label>
              {isPhone ? (
                <InputPhone
                  className={cn(styles.phoneInput)}
                  onChange={onPhoneChange}
                  value={phoneRef.current}
                  isValid={!phoneErr}
                  isVisibleError={false}
                  toHideErrorMessage={true}
                  onKeyDown={handleKeyDown}
                  showSuccess={false}
                />
              ) : (
                <input
                  onChange={onEmailChange}
                  className={styles.emailInput}
                  type="email"
                  name="email"
                  placeholder="example@domail.com"
                  onKeyDown={handleKeyDown}
                />
              )}
            </div>

            <button type="button" className={styles.switchBtn} onClick={switchSignUp}>
              {t(getSwitchName(referralAction))} {isPhone ? t('email') : t('phone')} <hr color="#1168f1" />
            </button>

            <div className={cn(styles.captchaWrapper, styles.verifyButtonContainer)}>
              <Captcha onCaptchaSuccess={getCode} setIsLoading={setIsLoading}>
                <div ref={codeBtnRef} className={styles.btnWrapper}>
                  {!isPhone && emailErr && <small className={styles.err}>{emailErr}</small>}
                  {isPhone && phoneErr && <small className={styles.phone__err}>{phoneErr}</small>}
                  <ButtonBlue isLoading={isLoading} className={styles.getCodeBtn}>
                    {t(getButtonName(referralAction))}
                  </ButtonBlue>
                </div>
              </Captcha>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h1>{t(getTitle(referralAction))}</h1>
            <p>
              {t('enter_code_it_was_send')}:{' '}
              {isPhone ? <span>{phoneRef.current}</span> : <span>{emailRef.current}</span>}
            </p>
            <form className={styles.formCode}>
              <CodeVerificationInput
                code={code}
                setCode={setCode}
                isCodeValid={isPhone ? isPhoneCodeValid : isEmailCodeValid}
                setIsCodeValid={isPhone ? setIsPhoneCodeValid : setIsEmailCodeValid}
                errorMsg={rateLimitErr}
                setErrorMsg={setRateLimitErr}
                isLoading={isLoading}
                countSeconds={TIMER_RESEND}
                resendBtn
                onResendCode={isPhone ? resendPhoneCode : resendEmailCode}
                verifyCode={isPhone ? sendPhoneCode : sendEmailCode}
                resendWithCaptcha
                showError
                withFooter={false}
                wrapperClassName={styles.inputCode}
              />

              <div className={styles.btnWrapper}>
                {(!isPhoneCodeValid || !isEmailCodeValid) && (
                  <small className={cn(styles.err, styles.err_code)}>
                    {rateLimitErr ?? t('incorrect_code_try_again')}
                  </small>
                )}
                <Captcha onCaptchaSuccess={requestNewCode} setIsLoading={setIsLoading}>
                  <button
                    className={cn(styles.requestBtn, { [styles.requestBtn_active]: countSeconds === 0 })}
                    type="button"
                  >
                    {isLoading ? (
                      <Loader className={styles.loader} />
                    ) : (
                      <>
                        {t('request_new_code')} {countSeconds !== 0 ? countSeconds : ''}
                      </>
                    )}
                  </button>
                </Captcha>
              </div>
            </form>
          </>
        )}

        {step === 3 && (
          <>
            <h1>{t('security_verification')}</h1>
            <p>
              {is2faGoogleApp ? (
                t('two_factor_auth')
              ) : (
                <>
                  {t('enter_code_it_was_send_to_backup_email')} <span>{backupEmail}</span>
                </>
              )}
            </p>

            <CodeVerificationInput
              code={code2fa}
              setCode={setCode2fa}
              isCodeValid={isValid2faCode}
              setIsCodeValid={setIsValid2faCode}
              errorMsg={rateLimitErr}
              setErrorMsg={setRateLimitErr}
              isLoading={isLoading}
              countSeconds={TIMER_RESEND}
              resendBtn
              verifyCode={send2faCode}
              resendWithCaptcha
              showError
              withFooter={false}
              wrapperClassName={styles.inputCode__2fa}
            />

            <div className={styles.btnWrapper}>
              {!isValid2faCode && (
                <small className={cn(styles.err, styles.err_2fa)}>
                  {rateLimitErr ?? t('incorrect_code_try_again')}
                </small>
              )}
            </div>

            <div className={styles.loaderWrapper}>{isLoading && <Loader className={styles.loader} />}</div>

            <section className={styles.footer}>
              <button
                type="button"
                disabled={!backupEmail}
                className={styles.switchSecBtn}
                onClick={() => setIs2faGoogleApp((prev) => !prev)}
              >
                {is2faGoogleApp ? t('use_backup_email') : t('use_auth_app')}
              </button>

              <div className={cn(styles.small, styles.securityContact)}>
                <div>
                  {t('need_help')}{' '}
                  <button type="button" onClick={goToChat}>
                    <a>{t('contact_support')}</a>
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </form>
      <img
        className={styles.formUnderlayer}
        src={screenWidth > 765 ? FormUnderlayer : FormUnderlayerMobile}
        alt="form underlayer"
      />
    </section>
  )
}
